import type {RouteName} from '../routes';
import {EDITION, SITE_NAME} from './site';

export type SectionKey =
	| 'index'
	| 'classes'
	| 'species'
	| 'origins'
	| 'feats'
	| 'spells'
	| 'items'
	| 'monsters'
	| 'rules'
	| 'wiki'
	| 'maps'
	| 'tools'
	| 'names'
	| 'spellbook'
	| 'books'
	| 'trans14to24';

type Section = {
	/** название раздела в хлебных крошках */
	name: string;
	/** заголовок h1 на странице раздела */
	heading: string;
	route: RouteName;
	title: string;
	description: string;
};

export const SECTIONS: Record<SectionKey, Section> = {
	index: {
		name: 'Главная',
		route: 'index',
		heading: 'Справочник D&D 5e 2024 на русском',
		title: `Справочник ${EDITION} на русском — ${SITE_NAME} — в терминологии Hobby World`,
		description:
			'Справочник по D&D 5e на русском языке по правилам 2024: классы и подклассы, виды, предыстории, черты, заклинания, магические предметы, чудовища с блоками параметров, правила, приключения и история мира.',
	},
	classes: {
		name: 'Классы',
		route: 'classes',
		heading: 'Классы',
		title: `Классы — все классы и подклассы и архетипы`,
		description:
			'Все классы D&D 5e по правилам 2024 на русском: особенности по уровням, таблицы классов, подклассы, архетипы, выбор класса и списки заклинаний классов.',
	},
	species: {
		name: 'Виды',
		route: 'species',
		heading: 'Виды (расы)',
		title: `Виды (расы)`,
		description:
			'Все виды (расы) персонажей D&D 5e по правилам 2024 на русском: тип существа, размер, скорость и особенности каждого вида.',
	},
	origins: {
		name: 'Предыстории',
		route: 'origins',
		heading: 'Предыстории',
		title: `Предыстории и происхождения`,
		description:
			'Все предыстории (происхождения) D&D 5e по правилам 2024 на русском: характеристики, черта происхождения, навыки, инструменты и снаряжение.',
	},
	feats: {
		name: 'Черты',
		route: 'feats',
		heading: 'Черты',
		title: `Черты — все черты`,
		description:
			'Все черты D&D 5e по правилам 2024 на русском: черты Происхождения, универсальные черты, черты Боевого стиля, Эпического дара и Планарного договора с требованиями и описанием.',
	},
	spells: {
		name: 'Заклинания',
		route: 'spells',
		heading: 'Заклинания',
		title: `Заклинания — список всех магических заклинаний`,
		description:
			'Все заклинания D&D 5e по правилам 2024 на русском: фокусы и заклинания 1–9 кругов. Фильтры по классу, кругу, школе, времени сотворения, типу урона и компонентам: словесным (вербальным), жестовым (соматическим) и материальным.',
	},
	items: {
		name: 'Предметы',
		route: 'items',
		heading: 'Магические предметы, снаряжение и экипировка',
		title: `Магические предметы, снаряжение и экипировка`,
		description:
			'Магические предметы, оружие, доспехи, снаряжение и экипировка D&D 5e по правилам 2024 на русском: редкость, настройка, цена, вес и свойства.',
	},
	monsters: {
		name: 'Чудовища',
		route: 'monsters',
		heading: 'Чудовища',
		title: `Чудовища — бестиарий с блоками параметров`,
		description:
			'Бестиарий D&D 5e по правилам 2024 на русском: блоки параметров (статблоки) чудовищ, класс опасности, класс брони, пункты здоровья, действия и описание.',
	},
	books: {
		name: 'Приключения',
		route: 'books',
		heading: 'Приключения',
		title: `Приключения D&D на русском — переводы книг приключений`,
		description:
			'Приключения D&D 5e на русском: книги приключений целиком, с иллюстрациями, картами, блоками параметров чудовищ и магическими предметами.',
	},
	rules: {
		name: 'Правила',
		route: 'rules',
		heading: 'Правила',
		title: `Правила — справочник правил D&D 5e`,
		description:
			'Правила D&D 5e по правилам 2024 на русском: проверки, бой, исследование, магия, отдых, урон и исцеление — всё о каждом правиле в одной статье.',
	},
	wiki: {
		name: 'История мира',
		route: 'wiki',
		heading: 'История мира',
		title: `История мира — города, страны и персонажи Забытых Королевств`,
		description:
			'История мира D&D на русском: статьи о городах, странах, народах, богах и персонажах Забытых Королевств и Фейруна.',
	},
	maps: {
		name: 'Карты',
		route: 'maps',
		heading: 'Карты',
		title: `Интерактивные карты Забытых Королевств`,
		description:
			'Интерактивные карты мира D&D на русском: Фейрун с городами, дорогами, лесами, горами и регионами, со ссылками на статьи об истории мира.',
	},
	tools: {
		name: 'Инструменты',
		route: 'tools',
		heading: 'Инструменты',
		title: `Инструменты мастера и игрока — генераторы имён и книг заклинаний`,
		description:
			'Инструменты для D&D 5e на русском: генератор имён для разных видов и народов, генератор книги заклинаний волшебника с расчётом стоимости.',
	},
	spellbook: {
		name: 'Генератор книги заклинаний',
		route: 'toolSpellbook',
		heading: 'Генератор книги заклинаний',
		title: `Генератор книги заклинаний волшебника — случайные заклинания и цена`,
		description:
			'Случайная книга заклинаний волшебника для D&D 5e на русском: выберите максимальный круг, и генератор подберёт заклинания, как у волшебника соответствующего уровня, и посчитает стоимость книги.',
	},
	names: {
		name: 'Имена',
		route: 'names',
		heading: 'Генератор имён',
		title: `Генератор имён — имена эльфов, дварфов, гномов и других видов и рас`,
		description:
			'Генератор случайных имён для D&D 5e на русском: мужские и женские имена, фамилии, названия кланов и прозвища эльфов, дварфов, гномов, полуросликов, драконорождённых, тифлингов, орков, людей, людоящеров, табакси и тритонов.',
	},
	trans14to24: {
		name: 'Перевод терминов 2014 в 2024',
		route: 'trans14to24',
		heading: 'Перевод терминов D&D 2014 в 2024',
		title: 'Перевод терминов D&D 2014 в 2024',
		description: 'Таблица соответствия терминов D&D 5e 2014 и 2024 на русском: как изменился перевод и почему.',
	},
};
